/**
 * DocumentSyncPanel — pulpit DOKUMENTÓW dla jednej integracji (FK).
 *
 * Dokumenty księgowe idą WYŁĄCZNIE jednokierunkowo: my → Symfonia FK (eksport). Zero pobierania
 * dokumentów z FK (decyzja produktowa, stała). Dlatego NIE ma tu importu ani lustra „zaimportowane".
 * Właściwa wysyłka dokumentu do FK (wejście biznesowe) dojdzie przy przepływie eksportu i będzie
 * wyzwalana z poziomu dokumentu / reguły automatyzacji — nie z tej karty.
 *
 * Wyrównujemy tylko WIZUALNIE: te same kafelki statystyk co przy kontrahentach/pracownikach, żeby
 * zakładka nie była gołą kartą.
 */

import { useTranslation } from 'react-i18next';

import { type ErpIntegrationDto } from './IntegrationsTab';

const PREFIX = 'tenant.settings.integrations.documentSync';

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('pl-PL');
}

export function DocumentSyncPanel({ integration }: { integration: ErpIntegrationDto }) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      {/* ── Statystyki (spójne z Kontrahentami/Pracownikami) ───────── */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Stat label={t(`${PREFIX}.stat.lastSync`)} value={formatDateTime(integration.lastSyncAt)} />
        <Stat
          label={t(`${PREFIX}.stat.status`)}
          badge={integration.isActive ? 'active' : 'inactive'}
          badgeText={
            integration.isActive
              ? t('tenant.settings.integrations.list.statusActive')
              : t('tenant.settings.integrations.list.statusInactive')
          }
        />
      </div>

      {/* ── Kierunek: tylko eksport (my → FK) ──────────────────────── */}
      <section className="rounded-lg border border-border-primary bg-bg-secondary p-4">
        <h4 className="text-sm font-semibold text-text-primary">
          {t(`${PREFIX}.exportOnly.title`)}
        </h4>
        <p className="mt-1 text-xs text-text-tertiary">{t(`${PREFIX}.exportOnly.hint`)}</p>
      </section>
    </div>
  );
}

function Stat({
  label,
  value,
  badge,
  badgeText,
}: {
  label: string;
  value?: string;
  badge?: 'active' | 'inactive';
  badgeText?: string;
}) {
  return (
    <div className="rounded-lg border border-border-primary bg-bg-secondary p-4">
      <div className="text-[0.68rem] font-medium uppercase tracking-wide text-text-tertiary">
        {label}
      </div>
      {badge ? (
        <span
          className={`mt-2 inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
            badge === 'active'
              ? 'bg-green-500/10 text-green-700 dark:text-green-400'
              : 'bg-bg-tertiary text-text-tertiary'
          }`}
        >
          {badgeText}
        </span>
      ) : (
        <div className="mt-1 text-lg font-semibold text-text-primary">{value}</div>
      )}
    </div>
  );
}
